import React, { createContext, useState, useContext, useEffect, useCallback, useMemo, useRef } from 'react';
import { interpolateWeekendData, getDATLeaders, fetchDailyGrowthData, fetchLatestTickerSnapshot, fetchLatestTimeSeriesSnapshot, fetchTickerTimeSeries, fetchAllTickersTimeSeries, fetchTickersTimeSeriesByAsset, fetchNAVGrowthByAsset, fetchAllSupplyData, fetchMarketCapAndVolumeByAsset } from '../services/dataService';
import companiesData from '../data/companies.json';

// Helper to get asset tickers (same logic as dataService)
const getAssetTickers = () => {
  if (!companiesData) {
    return { bitcoin: [], ethereum: [], solana: [] };
  }
  const pick = (arr) => (arr || []).filter(x => x.trackData === true).map(x => x.ticker);
  return {
    bitcoin: pick(companiesData.bitcoin || []),
    ethereum: pick(companiesData.ethereum || []),
    solana: pick(companiesData.solana || []),
  };
};

/**
 * GlobalDataContext - Manages global time series data to avoid repeated database calls
 * 
 * Features:
 * - Fetches all time series data once on app load
 * - Provides processed data for different views (leaderboards, charts, etc.)
 * - Automatic refresh capability
 * - Loading states and error handling
 */

const GlobalDataContext = createContext();

export const GlobalDataProvider = ({ children }) => {
  const [allTimeSeriesData, setAllTimeSeriesData] = useState(null); // Full historical data (null = not loaded yet)
  const [loadedAssets, setLoadedAssets] = useState([]); // Track which assets have been loaded (array instead of Set for React state)
  const [latestSnapshotData, setLatestSnapshotData] = useState(null); // Latest snapshot data
  const [latestTimeSeriesSnapshot, setLatestTimeSeriesSnapshot] = useState(null); // Latest time series snapshot data
  const [dailyGrowthData, setDailyGrowthData] = useState(null);
  // Overview aggregates
  const [totalMarketCap, setTotalMarketCap] = useState(null);
  const [cryptoHoldings, setCryptoHoldings] = useState(null);
  const [tickerHoldings, setTickerHoldings] = useState({});
  const [marketCapAndVolumeByAsset, setMarketCapAndVolumeByAsset] = useState(null);
  const [supplyPercentagesData, setSupplyPercentagesData] = useState(null);
  const [supplyPercentagesByAsset, setSupplyPercentagesByAsset] = useState({});
  const [navGrowthByAssetData, setNavGrowthByAssetData] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(0); // 0-100
  const [loadingMessage, setLoadingMessage] = useState('Loading Data');
  const [loadingStartTime, setLoadingStartTime] = useState(null); // Track when loading started
  const [assetLoadingProgress, setAssetLoadingProgress] = useState(0); // Separate progress for asset-specific loading
  const maxProgressRef = useRef(0); // Track maximum progress to prevent going backwards
  const loadingTimeSeriesRef = useRef(false); // Prevent concurrent time series loads
  const preloadTriggeredRef = useRef(false); // Track if preload has been triggered
  const preloadLazyDataRef = useRef(null); // Ref to store preload function
  const loadingFullTickerRef = useRef(new Set()); // Track which tickers are currently loading full data
  const queryCompletionRef = useRef(0); // Track query completion count for progress

  /**
   * Safely update progress - only increases, never decreases
   */
  const updateProgress = useCallback((newProgress) => {
    setLoadingProgress(prev => {
      // Always use the ref for the absolute maximum, but allow progress to increase based on new value
      const currentMax = Math.max(prev, maxProgressRef.current);
      const safeProgress = Math.max(currentMax, Math.min(100, newProgress));
      maxProgressRef.current = safeProgress;
      return safeProgress;
    });
  }, []);

  /**
   * Update asset-specific loading progress (can reset to 0)
   */
  const updateAssetProgress = useCallback((newProgress) => {
    setAssetLoadingProgress(Math.min(100, Math.max(0, newProgress)));
  }, []);

  // Helper to interpolate weekend data
  const interpolateData = useCallback((data, valueFields) => {
    if (!data || data.length < 2) return data;
    const iso = (d) => new Date(d).toISOString().split("T")[0];

    const filled = data.map(d => ({ ...d }));
    for (let i = 0; i < filled.length; i++) {
      const cur = filled[i];
      if (valueFields.some(f => cur[f] === 0)) {
        let prev = null, next = null;
        for (let j = i - 1; j >= 0; j--) {
          if (valueFields.some(f => (filled[j][f] || 0) > 0)) { prev = filled[j]; break; }
        }
        for (let j = i + 1; j < filled.length; j++) {
          if (valueFields.some(f => (filled[j][f] || 0) > 0)) { next = filled[j]; break; }
        }
        if (prev && next) {
          const pd = new Date(prev.date).valueOf();
          const nd = new Date(next.date).valueOf();
          const cd = new Date(cur.date).valueOf();
          const ratio = nd === pd ? 0 : (cd - pd) / (nd - pd);
          for (const f of valueFields) cur[f] = Math.round(prev[f] + (next[f] - prev[f]) * ratio);
        }
      }
    }

    const out = [];
    for (let i = 0; i < filled.length - 1; i++) {
      const a = filled[i];
      const b = filled[i + 1];
      const da = new Date(a.date);
      const db = new Date(b.date);
      const diff = Math.floor((+db - +da) / 86400000);
      out.push(a);
      if (diff > 1) {
        for (let d = 1; d < diff; d++) {
          const dt = new Date(+da + d * 86400000);
          const ratio = d / diff;
          const row = { date: iso(dt) };
          for (const f of valueFields) row[f] = Math.round(a[f] + (b[f] - a[f]) * ratio);
          out.push(row);
        }
      }
    }
    out.push(filled[filled.length - 1]);
    return out;
  }, []);

  /**
   * Fetch all time series data from Supabase
   * Optimized for fast initial render - loads essential data first, then progressively enhances
   */
  const fetchAllTimeSeriesData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      maxProgressRef.current = 0; // Reset max progress
      queryCompletionRef.current = 0; // Reset query completion counter
      setLoadingProgress(0);
      setLoadingStartTime(Date.now()); // Track when loading started
      setLoadingMessage('Loading Data');
      
      // ============================================
      // START ALL QUERIES IMMEDIATELY (Parallel)
      // ============================================
      updateProgress(5);
      setLoadingMessage('Loading all data...');
      
      // Track completion count for sequential progress updates
      // Note: fetchMarketCapAndVolumeByAsset and fetchNAVGrowthByAsset both use fetchAllGrowthAssetData
      // So we have 3 independent queries, not 4
      queryCompletionRef.current = 0; // Reset counter
      const totalQueries = 3; // snapshot, marketCap/volume+navGrowth (shared), supply
      const progressStart = 5;
      const progressEnd = 90;
      const progressPerQuery = (progressEnd - progressStart) / totalQueries;
      
      // Track if growth_asset query has completed (shared by mcap/volume and nav growth)
      let growthAssetCompleted = false;
      
      const updateQueryProgress = () => {
        // Increment atomically
        const currentCount = queryCompletionRef.current + 1;
        queryCompletionRef.current = currentCount;
        const newProgress = progressStart + (currentCount * progressPerQuery);
        // Force immediate update - use functional update to ensure we get latest state
        setLoadingProgress(prev => {
          const safeProgress = Math.max(prev, Math.min(newProgress, progressEnd));
          maxProgressRef.current = Math.max(maxProgressRef.current, safeProgress);
          return safeProgress;
        });
      };
      
      const updateGrowthAssetProgress = () => {
        // Only count growth_asset completion once, even though it's used by two functions
        if (!growthAssetCompleted) {
          growthAssetCompleted = true;
          updateQueryProgress();
        }
      };
      
      // Start ALL queries in parallel - no waiting!
      const allPromises = [];
      
      // Query 1: Latest snapshot (needed for initial render)
      const snapshotPromise = fetchLatestTickerSnapshot()
        .then(data => {
          if (data && data.length > 0) {
            setLatestSnapshotData(data);
            
            // Compute totals from snapshot synchronously (fast - no queries)
            const at = getAssetTickers();
            const tickerHoldingsMap = { bitcoin: {}, ethereum: {}, solana: {} };
            
            for (const assetType of ['bitcoin', 'ethereum', 'solana']) {
              const tickers = at[assetType];
              for (const ticker of tickers) {
                const row = data.find(r => r.ticker === ticker);
                if (row && row.crypto_per_share !== undefined && row.outstanding_shares_estimated !== undefined) {
                  const holdings = (row.crypto_per_share || 0) * (row.outstanding_shares_estimated || 0);
                  tickerHoldingsMap[assetType][ticker] = holdings;
                }
              }
            }
            
            setTickerHoldings(tickerHoldingsMap);
            
            // Compute crypto holdings USD
            const prices = { BTC: 50000, ETH: 3000, SOL: 100 };
            const btcTotal = Object.values(tickerHoldingsMap.bitcoin).reduce((sum, h) => sum + (h || 0), 0);
            const ethTotal = Object.values(tickerHoldingsMap.ethereum).reduce((sum, h) => sum + (h || 0), 0);
            const solTotal = Object.values(tickerHoldingsMap.solana).reduce((sum, h) => sum + (h || 0), 0);
            
            const btcUSD = btcTotal * prices.BTC;
            const ethUSD = ethTotal * prices.ETH;
            const solUSD = solTotal * prices.SOL;
            const totalUSD = btcUSD + ethUSD + solUSD || 1;
            
            setCryptoHoldings({
              totalUSD,
              bitcoin: { holdings: btcTotal, usdValue: btcUSD, percentage: (btcUSD / totalUSD) * 100 },
              ethereum: { holdings: ethTotal, usdValue: ethUSD, percentage: (ethUSD / totalUSD) * 100 },
              solana: { holdings: solTotal, usdValue: solUSD, percentage: (solUSD / totalUSD) * 100 },
            });
            
            // Compute total market cap from snapshot - only include tracked tickers
            const allTrackedTickers = [...at.bitcoin, ...at.ethereum, ...at.solana];
            const totalMcap = data
              .filter(row => allTrackedTickers.includes(row.ticker))
              .reduce((sum, r) => sum + (r.mcap_estimated || 0), 0);
            setTotalMarketCap(totalMcap);
          }
          updateQueryProgress();
          return data;
        })
        .catch(() => {
          updateQueryProgress();
          return [];
        });
      allPromises.push(snapshotPromise);

      // Query 2: Market cap and volume (uses growth_asset)
      const mcapVolumePromise = fetchMarketCapAndVolumeByAsset()
        .then(data => {
          setMarketCapAndVolumeByAsset(data);
          updateGrowthAssetProgress(); // Counts as growth_asset completion
          return data;
        })
        .catch(() => {
          updateGrowthAssetProgress();
          return [];
        });
      allPromises.push(mcapVolumePromise);

      // Query 3: Supply data
      const supplyPromise = fetchAllSupplyData()
        .then(data => {
          setSupplyPercentagesData(data?.supplyDaily || []);
          
          // Ensure supplyPercentagesByAsset always has the expected structure
          const assetTotals = data?.assetTotals || {};
          setSupplyPercentagesByAsset({ 
            bitcoin: assetTotals?.bitcoin?.tickerPercentages || {}, 
            ethereum: assetTotals?.ethereum?.tickerPercentages || {}, 
            solana: assetTotals?.solana?.tickerPercentages || {} 
          });
          updateQueryProgress();
          return data;
        })
        .catch(() => {
          updateQueryProgress();
          return { supplyDaily: [], assetTotals: {} };
        });
      allPromises.push(supplyPromise);

      // Query 4: NAV growth (uses same growth_asset query as Query 2)
      const navGrowthPromise = fetchNAVGrowthByAsset()
        .then(data => {
          setNavGrowthByAssetData(data);
          updateGrowthAssetProgress(); // Only counts if not already counted
          return data;
        })
        .catch(() => {
          updateGrowthAssetProgress();
          return [];
        });
      allPromises.push(navGrowthPromise);
      
      // Wait for all queries to complete
      await Promise.allSettled(allPromises);
      
      updateProgress(95);
      setLoadingMessage('Finalizing...');
      setLastFetchTime(new Date());
      
      // ============================================
      // PHASE 4: Daily Growth Data - LAZY LOAD ONLY
      // ============================================
      // NOTE: Growth data is 250 kB and takes ~9-18 seconds!
      // We DON'T load it on initial page load - it will be loaded lazily
      // when user actually needs it (e.g., viewing growth charts)
      // This saves ~9-18 seconds on initial load!
      
      // ============================================
      // ALL DATA LOADED - Hide loading bar now
      // ============================================
      updateProgress(100);
      setLoadingMessage('Complete!');
      
      // Small delay to show 100% before hiding progress
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Now hide loading bar - everything is loaded
      setLoading(false);
      setLoadingProgress(0);
      setLoadingStartTime(null); // Reset loading start time
      setLoadingMessage('Loading Data');
      
      // ============================================
      // Load latest_series snapshot in background (non-blocking)
      // ============================================
      // Start loading latest_series snapshot after landing page is done
      // This is used for CompanyDetail pro forma data, but we load it proactively
      // Don't await - let it load in the background without blocking UI
      // Check if already loaded to avoid duplicate requests
      if (latestTimeSeriesSnapshot === null) {
        fetchLatestTimeSeriesSnapshot()
          .then(data => {
            if (data && data.length > 0) {
              setLatestTimeSeriesSnapshot(data);
            } else {
              setLatestTimeSeriesSnapshot([]); // Mark as loaded even if empty
            }
          })
          .catch(() => {
            setLatestTimeSeriesSnapshot([]); // Mark as loaded even on error
          });
      }
      
    } catch (err) {
      setError(err.message);
      setLoading(false);
      maxProgressRef.current = 0;
      setLoadingProgress(0);
      setLoadingStartTime(null); // Reset loading start time on error
      setLoadingMessage('Loading Data');
    }
  }, [updateProgress, interpolateData]);

  /**
   * Get latest data for each ticker
   */
  const getLatestDataByTicker = useCallback(() => {
    if (!latestSnapshotData) return {};

    const tickerMap = new Map();
    latestSnapshotData.forEach(row => {
      if (!tickerMap.has(row.ticker)) {
        tickerMap.set(row.ticker, row);
      }
    });
    
    const result = Object.fromEntries(tickerMap);
    return result;
  }, [latestSnapshotData]);

  /**
   * Get DAT leaders for trading volume and transaction count
   */
  const getDATLeadersData = useCallback(async () => {
    try {
      return await getDATLeaders();
    } catch (error) {
      return {
        tradingVolumeLeader: null,
        transactionCountLeader: null,
        allDATCompanies: []
      };
    }
  }, []);

  /**
   * Get market cap leaderboard data
   */
  const getMarketCapLeaderboard = useCallback(() => {
    // Use snapshot data directly - it's already latest per ticker
    if (!latestSnapshotData) return [];
    
    // Get latest per ticker from time series
    const latestByTicker = {};
    latestSnapshotData.forEach(row => {
      if (!latestByTicker[row.ticker]) {
        latestByTicker[row.ticker] = row;
      }
    });
    
    return Object.values(latestByTicker)
      .map(row => ({
        ticker: row.ticker,
        market_cap: row.mcap_estimated || 0,
        date: row.date
      }))
      .sort((a, b) => (b.market_cap || 0) - (a.market_cap || 0));
  }, [latestSnapshotData]);

  /**
   * Get price growth leaderboard data
   */
  const getPriceGrowthLeaderboard = useCallback(() => {
    const latestData = getLatestDataByTicker();
    
    return Object.values(latestData)
      .map(row => ({
        ticker: row.ticker,
        price_growth: row.price_growth || 0,
        date: row.date
      }))
      .sort((a, b) => (b.price_growth || 0) - (a.price_growth || 0));
  }, [getLatestDataByTicker]);

  /**
   * Get premium leaderboard data
   */
  const getPremiumLeaderboard = useCallback(() => {
    // Use snapshot data directly - it's already latest per ticker
    if (!latestSnapshotData) return [];
    
    // Get latest per ticker from time series
    const latestByTicker = {};
    latestSnapshotData.forEach(row => {
      if (!latestByTicker[row.ticker]) {
        latestByTicker[row.ticker] = row;
      }
    });
    
    return Object.values(latestByTicker)
      .map(row => ({
        ticker: row.ticker,
        premium_discount: row.premium_discount || 0,
        date: row.date
      }))
      .sort((a, b) => (b.premium_discount || 0) - (a.premium_discount || 0));
  }, [latestSnapshotData]);

  /**
   * Lazy load time series data when needed (e.g., when navigating to asset tabs)
   * Loads tickers for a specific asset (bitcoin, ethereum, solana) or all if no asset specified
   */
  const loadTimeSeriesData = useCallback(async (assetType = null) => {
    // If loading all assets and already loaded, return
    if (!assetType && allTimeSeriesData !== null) return;
    
    // If loading specific asset and already loaded, return
    if (assetType && loadedAssets.includes(assetType)) {
      return;
    }
    
    // If already loading, return
    if (loadingTimeSeriesRef.current) {
      return;
    }
    
    try {
      loadingTimeSeriesRef.current = true;
      
      // Store initial progress if global loading is complete
      let newData = [];
      if (assetType) {
        // Load only for this asset with progress tracking
        // Reset asset loading progress to 0 when starting
        updateAssetProgress(0);
        
        // Create progress callback for asset-specific loading
        const onProgress = (progress) => {
          // Update asset-specific progress
          updateAssetProgress(progress);
        };
        
        newData = await fetchTickersTimeSeriesByAsset(assetType, onProgress);
        setLoadedAssets(prev => prev.includes(assetType) ? prev : [...prev, assetType]);
        
        // Set progress to 100% when done
        updateAssetProgress(100);
      } else {
        // Load all assets
        newData = await fetchAllTickersTimeSeries();
        setLoadedAssets(['bitcoin', 'ethereum', 'solana']);
      }
      
      // Merge with existing data
      if (allTimeSeriesData && allTimeSeriesData.length > 0) {
        // Create a map to avoid duplicates
        const existingMap = new Map();
        allTimeSeriesData.forEach(row => {
          const key = `${row.ticker}-${row.date}`;
          if (!existingMap.has(key)) {
            existingMap.set(key, row);
          }
        });
        
        // Add new data
        newData.forEach(row => {
          const key = `${row.ticker}-${row.date}`;
          if (!existingMap.has(key)) {
            existingMap.set(key, row);
          }
        });
        
        setAllTimeSeriesData(Array.from(existingMap.values()));
      } else {
        setAllTimeSeriesData(newData);
      }
      
      // Reset asset progress when done (after a brief delay to show 100%)
      if (!loading && assetType) {
        setTimeout(() => {
          updateAssetProgress(0);
        }, 300);
      }
    } catch (error) {
      if (!allTimeSeriesData) {
        setAllTimeSeriesData([]);
      }
      // Clear loading message on error too
      if (!loading) {
        setLoadingMessage('Loading Data');
      }
    } finally {
      loadingTimeSeriesRef.current = false;
    }
  }, [allTimeSeriesData, loadedAssets, interpolateData, loading, updateProgress, updateAssetProgress]);


  /**
   * Load full time series data for a specific ticker (for company detail pages)
   * This fetches all columns (not just lightweight ones) and replaces any existing lightweight data
   */
  const loadFullTickerTimeSeries = useCallback(async (ticker) => {
    if (!ticker) return;
    
    // Prevent concurrent loads for the same ticker
    if (loadingFullTickerRef.current.has(ticker)) {
      return;
    }
    
    // Check if we already have full data for this ticker
    const existingData = allTimeSeriesData?.filter(row => row.ticker === ticker) || [];
    if (existingData.length > 0) {
      // Check if we have full columns (nav_usd, premium_discount, trading_volume_shares are full-only columns)
      const hasFullData = existingData.some(row => 
        row.hasOwnProperty('nav_usd') || 
        row.hasOwnProperty('premium_discount') || 
        row.hasOwnProperty('trading_volume_shares')
      );
      
      if (hasFullData) {
        // Already have full data, no need to fetch
        return;
      }
    }
    
    // Mark as loading
    loadingFullTickerRef.current.add(ticker);
    
    try {
      // Fetch full time series data for this ticker
      const fullData = await fetchTickerTimeSeries(ticker);
      
      if (!fullData || fullData.length === 0) {
        return;
      }
      
      // Interpolate weekend data
      const interpolatedData = await interpolateWeekendData(fullData, [
        'mcap_estimated', 'nav_usd', 'premium_discount', 'crypto_per_share', 
        'outstanding_shares_estimated', 'trading_volume_shares', 'mnav', 
        'stock_price_close', 'nav_per_share', 'trading_tx_count', 'turnover', 'crypto_holdings'
      ]);
      
      // Replace lightweight data with full data for this ticker
      if (allTimeSeriesData && allTimeSeriesData.length > 0) {
        // Remove existing data for this ticker and add full data
        const filteredData = allTimeSeriesData.filter(row => row.ticker !== ticker);
        setAllTimeSeriesData([...filteredData, ...interpolatedData]);
      } else {
        // No existing data, just set the full data
        setAllTimeSeriesData(interpolatedData);
      }
    } catch (error) {
      // Error loading full data
    } finally {
      // Remove from loading set - THIS IS CRITICAL!
      loadingFullTickerRef.current.delete(ticker);
    }
  }, [allTimeSeriesData]);

  /**
   * Check if a specific ticker's full time series data is currently loading
   */
  const isTickerLoading = useCallback((ticker) => {
    if (!ticker) return false;
    return loadingFullTickerRef.current.has(ticker);
  }, []);

  /**
   * Get time series data for a specific ticker
   */
  const getTickerTimeSeries = useCallback((ticker) => {
    if (allTimeSeriesData && allTimeSeriesData.length > 0) {
      const filtered = allTimeSeriesData.filter(row => row.ticker === ticker);
      return filtered.sort((a, b) => new Date(a.date) - new Date(b.date));
    }
    
    return [];
  }, [allTimeSeriesData]);

  /**
   * Get time series data grouped by asset type
   * Uses growth_asset table for market cap and trading volume (loaded in Phase 3)
   * Falls back to processing allTimeSeriesData if needed for other data
   */
  const getTimeSeriesByAsset = useCallback(() => {
    // Use market cap and volume from growth_asset table (loaded in Phase 3)
    if (marketCapAndVolumeByAsset && marketCapAndVolumeByAsset.length > 0) {
      const startDate = '2025-02-20';
      return marketCapAndVolumeByAsset.filter(item => item.date >= startDate);
    }
    
    // Fallback: if we have time series data, process it (for other use cases)
    if (allTimeSeriesData && allTimeSeriesData.length > 0) {
      const startDate = '2025-02-20';
      
      // Group data by date and sum by asset type
      const groupedByDate = {};
      
      allTimeSeriesData.forEach(row => {
        const date = row.date;
        // Filter out dates before Feb 20, 2025
        if (date < startDate) return;
        
        if (!groupedByDate[date]) {
          groupedByDate[date] = {
            date,
            btc_volume: 0,
            eth_volume: 0,
            sol_volume: 0,
            btc_mcap: 0,
            eth_mcap: 0,
            sol_mcap: 0,
            btc_nav: 0,
            eth_nav: 0,
            sol_nav: 0
          };
        }

        // Determine asset type based on ticker
        const ticker = row.ticker;
        if (ticker === 'MSTR' || ticker === 'SMLR' || ticker === 'CEP' || ticker === 'NAKA') {
          groupedByDate[date].btc_volume += row.trading_volume_shares || 0;
          groupedByDate[date].btc_mcap += row.mcap_estimated || 0;
          groupedByDate[date].btc_nav += row.nav_usd || 0;
        } else if (ticker === 'BMNR' || ticker === 'SBET' || ticker === 'BTCS' || ticker === 'BTBT' || ticker === 'ETHZ' || ticker === 'ETHM' || ticker === 'FGNX') {
          groupedByDate[date].eth_volume += row.trading_volume_shares || 0;
          groupedByDate[date].eth_mcap += row.mcap_estimated || 0;
          groupedByDate[date].eth_nav += row.nav_usd || 0;
        } else if (ticker === 'HSDT' || ticker === 'FORD' || ticker === 'DFDV' || ticker === 'UPXI' || ticker === 'STSS') {
          groupedByDate[date].sol_volume += row.trading_volume_shares || 0;
          groupedByDate[date].sol_mcap += row.mcap_estimated || 0;
          groupedByDate[date].sol_nav += row.nav_usd || 0;
        }
      });

      const sortedData = Object.values(groupedByDate).sort((a, b) => new Date(a.date) - new Date(b.date));
      
      // Normalize trading volume data to percentages
      const normalizedData = sortedData.map(row => {
        const totalVolume = row.btc_volume + row.eth_volume + row.sol_volume;
        if (totalVolume > 0) {
          return {
            ...row,
            btc_volume: (row.btc_volume / totalVolume) * 100,
            eth_volume: (row.eth_volume / totalVolume) * 100,
            sol_volume: (row.sol_volume / totalVolume) * 100,
          };
        }
        return row;
      });
      
      // Return normalized data (interpolation is handled by fetchMarketCapAndVolumeByAsset)
      // For fallback case, interpolation would need to be handled separately if needed
      return normalizedData;
    }
    
    // Return empty if no data available
    return [];
  }, [marketCapAndVolumeByAsset, allTimeSeriesData]);

  /**
   * Get latest daily growth data for each ticker
   */
  const getLatestDailyGrowthByTicker = useCallback(() => {
    if (!dailyGrowthData) return {};

    const tickerMap = new Map();
    dailyGrowthData.forEach(row => {
      if (!tickerMap.has(row.ticker)) {
        tickerMap.set(row.ticker, row);
      }
    });

    return Object.fromEntries(tickerMap);
  }, [dailyGrowthData]);

  /**
   * Lazy load latest time series snapshot when needed (only used for CompanyDetail pro forma)
   */
  const loadLatestTimeSeriesSnapshot = useCallback(async () => {
    if (latestTimeSeriesSnapshot !== null) return; // Already loaded
    
    try {
      const data = await fetchLatestTimeSeriesSnapshot();
      if (data && data.length > 0) {
        setLatestTimeSeriesSnapshot(data);
      } else {
        setLatestTimeSeriesSnapshot([]); // Mark as loaded even if empty
      }
    } catch (err) {
      setLatestTimeSeriesSnapshot([]); // Mark as loaded even on error
    }
  }, [latestTimeSeriesSnapshot]);

  /**
   * Lazy load daily growth data when needed (heavy query - 250 kB)
   * This is called automatically when getTickerDailyGrowth or getDailyGrowthLeaderboard is used
   */
  const loadDailyGrowthData = useCallback(async () => {
    if (dailyGrowthData !== null) return; // Already loaded
    
    try {
      setLoadingMessage('Loading growth data...');
      const result = await fetchDailyGrowthData().catch(() => []);
      if (result && result.length > 0) {
        setDailyGrowthData(result);
      }
    } catch (err) {
      // Error loading daily growth data
    }
  }, [dailyGrowthData]);

  // Store lazy load functions in ref for preloadLazyData to use
  useEffect(() => {
    preloadLazyDataRef.current = {
      loadLatestSeries: loadLatestTimeSeriesSnapshot
      // Note: loadTimeSeries removed - now loaded asset-specific on tab click
      // Note: loadDailyGrowth removed - daily_growth table is not used anymore
    };
  }, [loadLatestTimeSeriesSnapshot]);

  /**
   * Preload all lazy-loaded data items (called proactively on tab click)
   * This preloads: latestTimeSeriesSnapshot (only used for CompanyDetail pro forma)
   * Note: Time series data is now loaded asset-specific when tabs are clicked
   * Note: Daily growth data is no longer preloaded - it's not used on any pages
   */
  const preloadLazyData = useCallback(async () => {
    // Only preload once (check ref to avoid duplicate calls)
    if (preloadTriggeredRef.current) return;
    preloadTriggeredRef.current = true;
    
    // Load lazy items in parallel (they have their own promise caches, so safe to call multiple times)
    // Use the ref functions to avoid circular dependency issues
    // Note: We don't preload time series data anymore - it's loaded asset-specific on tab click
    // Note: We don't preload daily growth data - it's not used on any pages
    if (preloadLazyDataRef.current) {
      const { loadLatestSeries } = preloadLazyDataRef.current;
      Promise.all([
        loadLatestSeries().catch(() => {})
      ]);
    }
  }, []);

  /**
   * Get daily growth data for a specific ticker
   * NOTE: Daily growth data is no longer loaded - this function returns empty array
   * The daily_growth table is not used on any pages anymore
   */
  const getTickerDailyGrowth = useCallback((ticker) => {
    // Return empty array - daily growth data is not used anymore
    return [];
  }, []);

  /**
   * Get daily growth leaderboard data
   * NOTE: Daily growth data is no longer loaded - this function returns empty array
   * The daily_growth table is not used on any pages anymore
   */
  const getDailyGrowthLeaderboard = useCallback(() => {
    // Return empty array - daily growth data is not used anymore
    return [];
  }, []);

  /**
   * Get asset holdings for a specific asset
   */
  const getAssetHoldings = useCallback((assetType) => {
    const holdings = tickerHoldings[assetType] || {};
    const totalHoldings = Object.values(holdings).reduce((sum, h) => sum + (h || 0), 0);
    return { totalHoldings, tickerHoldings: holdings, assetType };
  }, [tickerHoldings]);

  /**
   * Get asset market cap for a specific asset
   */
  const getAssetMarketCap = useCallback((assetType) => {
    if (!latestSnapshotData) return 0;
    // Use dynamic ticker list from companies.json instead of hardcoded values
    const assetTickers = getAssetTickers();
    const targetTickers = assetTickers[assetType] || [];
    const total = latestSnapshotData
      .filter(row => targetTickers.includes(row.ticker))
      .reduce((sum, row) => sum + (row.mcap_estimated || 0), 0);
    
    return total;
  }, [latestSnapshotData]);

  /**
   * Get asset supply percentages for a specific asset
   */
  const getAssetSupplyPercentages = useCallback((assetType) => {
    if (!supplyPercentagesByAsset || typeof supplyPercentagesByAsset !== 'object') {
      return { totalPercentage: 0, tickerPercentages: {}, assetType };
    }
    const percentages = supplyPercentagesByAsset[assetType] || {};
    const totalPercentage = Object.values(percentages).reduce((sum, p) => sum + (p || 0), 0);
    return { totalPercentage, tickerPercentages: percentages, assetType };
  }, [supplyPercentagesByAsset]);

  // Fetch data on mount
  useEffect(() => {
    fetchAllTimeSeriesData();
  }, [fetchAllTimeSeriesData]);

  // Manual refresh function
  const refreshData = useCallback(async () => {
    await fetchAllTimeSeriesData();
  }, [fetchAllTimeSeriesData]);

  /**
   * Auto-refresh data if it's been more than 2 hours since last refresh
   * Checks periodically and when page becomes visible
   */
  useEffect(() => {
    const checkAndRefreshIfNeeded = () => {
      if (!lastFetchTime) return; // No previous fetch time, skip check
      
      const now = new Date();
      const lastFetch = new Date(lastFetchTime);
      
      // Calculate time difference in milliseconds
      const timeDiff = now.getTime() - lastFetch.getTime();
      const hoursSinceLastFetch = timeDiff / (1000 * 60 * 60); // Convert to hours
      
      // Refresh if it's been more than 2 hours
      if (hoursSinceLastFetch >= 2) {
        refreshData();
      }
    };

    // Check immediately on mount/update
    checkAndRefreshIfNeeded();

    // Set up interval to check every 30 minutes
    // This ensures we catch the 2-hour mark reasonably quickly
    const intervalId = setInterval(checkAndRefreshIfNeeded, 1800000); // Check every 30 minutes

    // Also check when the page becomes visible (user returns to tab)
    // This catches cases where the app was left open for a while
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkAndRefreshIfNeeded();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [lastFetchTime, refreshData]);

  /**
   * Check if an asset's time series data is loaded
   * Returns true if the asset is in loadedAssets AND has data for its tickers
   */
  const isAssetDataLoaded = useCallback((assetType) => {
    // If no asset type specified, check if all assets are loaded
    if (!assetType) {
      return loadedAssets.length === 3 && loadedAssets.includes('bitcoin') && 
             loadedAssets.includes('ethereum') && loadedAssets.includes('solana');
    }
    
    // Check if this specific asset is loaded
    if (!loadedAssets.includes(assetType)) {
      return false;
    }
    
    // Also verify that we actually have data for at least one ticker of this asset
    if (!allTimeSeriesData || allTimeSeriesData.length === 0) {
      return false;
    }
    
    // Get tickers for this asset
    const at = getAssetTickers();
    const tickers = assetType === 'bitcoin' ? at.bitcoin :
                    assetType === 'ethereum' ? at.ethereum :
                    assetType === 'solana' ? at.solana : [];
    
    // Check if we have data for at least one ticker
    return tickers.some(ticker => {
      const tickerData = allTimeSeriesData.filter(row => row.ticker === ticker);
      return tickerData && tickerData.length > 0;
    });
  }, [loadedAssets, allTimeSeriesData]);

  const value = useMemo(() => ({
    // Raw data
    allTimeSeriesData,
    latestSnapshotData, // Add latestSnapshotData to the context value
    latestTimeSeriesSnapshot,
    dailyGrowthData,
    // Overview aggregates
    totalMarketCap,
    cryptoHoldings,
    tickerHoldings,
    marketCapAndVolumeByAsset,
    supplyPercentagesData,
    supplyPercentagesByAsset,
    navGrowthByAssetData,
    loading,
    loadingProgress,
    assetLoadingProgress, // Expose asset loading progress separately
    loadingMessage,
    loadingStartTime, // Track when loading started for progress adjustment
    error,
    lastFetchTime,
    loadedAssets, // Expose loaded assets so components can check
    
    // Actions
    fetchAllTimeSeriesData,
    refreshData,
    loadTimeSeriesData,
    loadFullTickerTimeSeries, // Load full time series data for a specific ticker (for company detail pages)
    loadLatestTimeSeriesSnapshot, // Lazy load latest time series snapshot when needed
    loadDailyGrowthData, // Lazy load growth data when needed
    preloadLazyData, // Preload all lazy data items (called proactively)
    
    // Processed data getters
    getLatestDataByTicker,
    getDATLeadersData,
    getMarketCapLeaderboard,
    getPriceGrowthLeaderboard,
    getPremiumLeaderboard,
    getTickerTimeSeries,
    isAssetDataLoaded, // Helper to check if asset data is loaded
    isTickerLoading, // Check if a specific ticker's full data is loading
    getTimeSeriesByAsset,
    
    // Daily growth data getters
    getLatestDailyGrowthByTicker,
    getTickerDailyGrowth,
    getDailyGrowthLeaderboard,
    
    // Asset-level data getters
    getAssetHoldings,
    getAssetMarketCap,
    getAssetSupplyPercentages
  }), [
    allTimeSeriesData,
    latestSnapshotData, // Add latestSnapshotData to the dependency array
    latestTimeSeriesSnapshot,
    dailyGrowthData,
    totalMarketCap,
    cryptoHoldings,
    tickerHoldings,
    marketCapAndVolumeByAsset,
    supplyPercentagesData,
    supplyPercentagesByAsset,
    navGrowthByAssetData,
    loading,
    loadingProgress,
    assetLoadingProgress, // Add assetLoadingProgress to dependency array
    loadingMessage,
    error,
    lastFetchTime,
    loadedAssets, // Add loadedAssets to dependency array
    fetchAllTimeSeriesData,
    refreshData,
    loadTimeSeriesData,
    loadFullTickerTimeSeries,
    loadLatestTimeSeriesSnapshot,
    loadDailyGrowthData,
    preloadLazyData,
    getLatestDataByTicker,
    getDATLeadersData,
    isAssetDataLoaded, // Add isAssetDataLoaded to dependency array
    getMarketCapLeaderboard,
    getPriceGrowthLeaderboard,
    getPremiumLeaderboard,
    getTickerTimeSeries,
    getTimeSeriesByAsset,
    getLatestDailyGrowthByTicker,
    getTickerDailyGrowth,
    getDailyGrowthLeaderboard,
    getAssetHoldings,
    getAssetMarketCap,
    getAssetSupplyPercentages,
    loadingStartTime,
    isTickerLoading
  ]);

  return (
    <GlobalDataContext.Provider value={value}>
      {children}
    </GlobalDataContext.Provider>
  );
};

/**
 * Hook to use the global data context
 */
export const useGlobalData = () => {
  const context = useContext(GlobalDataContext);
  if (!context) {
    throw new Error('useGlobalData must be used within a GlobalDataProvider');
  }
  return context;
};

export default GlobalDataContext;
